/**
 * Gravar áudio no navegador (H16b).
 *
 * ── O formato é negociado, e não escolhido ────────────────────────────────
 *
 * Cada navegador grava no que sabe: o Chrome faz `audio/webm`, o Safari faz
 * `audio/mp4`, o Firefox faz `audio/ogg`. E o Chrome grava WebM **mesmo quando
 * se pede** `audio/ogg;codecs=opus` — pedir não basta, é preciso perguntar.
 *
 * Então este módulo pergunta, na ordem que interessa: primeiro os formatos que
 * o CANAL aceita (e aí o servidor não precisa converter nada), depois o que o
 * navegador oferecer. O que sobra o servidor converte.
 *
 * O caminho feliz é o Firefox e o Safari, que gravam direto em algo aceito. O
 * Chrome quase sempre cai na conversão — e é por isso que ela existe.
 *
 * ── O microfone precisa ser DEVOLVIDO ─────────────────────────────────────
 *
 * `getUserMedia` acende a luz da câmera/microfone e a mantém acesa até as
 * trilhas serem paradas. Parar só o `MediaRecorder` não basta: a luz continua,
 * e a pessoa vê o navegador dizendo que o site está ouvindo — depois de ela ter
 * cancelado a gravação. É o tipo de coisa que destrói confiança.
 *
 * Por isso `parar` e `cancelar` param as trilhas, e os dois passam pelo mesmo
 * lugar.
 */

/**
 * Os formatos em que vale tentar gravar, do melhor para o pior.
 *
 * `audio/mp4` primeiro porque é o que o Safari faz nativamente e a Meta aceita;
 * `ogg` em seguida, que é o formato de nota de voz do WhatsApp; `webm` por
 * último, que é o do Chrome e o único que sempre precisa de conversão.
 */
const FORMATOS = [
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/webm;codecs=opus',
  'audio/webm',
] as const;

export interface GravacaoEmCurso {
  /** Encerra e devolve o arquivo. */
  readonly parar: () => Promise<File>;
  /** Descarta. Não devolve nada, e devolve o microfone do mesmo jeito. */
  readonly cancelar: () => void;
}

/** O navegador consegue gravar? Falso em contexto sem HTTPS, e em navegador velho. */
export function podeGravar(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices?.getUserMedia !== undefined
  );
}

/**
 * O melhor formato que este navegador grava.
 *
 * `aceitos` é a lista do canal: quando um formato aceito também é suportado,
 * ele ganha — e aí o áudio sai pronto, sem passar pelo conversor do servidor.
 * `null` significa "o canal não tem regra", e aí qualquer suportado serve.
 */
export function melhorFormato(aceitos: readonly string[] | null): string | null {
  const suportados = FORMATOS.filter((formato) => MediaRecorder.isTypeSupported(formato));
  if (suportados.length === 0) return null;

  if (aceitos !== null) {
    const preferido = suportados.find((formato) =>
      aceitos.some((aceito) => aceito.toLowerCase() === semParametros(formato)),
    );
    if (preferido) return preferido;
  }

  return suportados[0] ?? null;
}

/** `audio/ogg;codecs=opus` → `audio/ogg`. A lista do canal não tem parâmetros. */
function semParametros(mime: string): string {
  return (mime.split(';')[0] ?? mime).trim().toLowerCase();
}

export class MicrofoneNegadoError extends Error {
  constructor() {
    super(
      'O navegador não liberou o microfone. Autorize o acesso na barra de endereço e tente ' +
        'de novo.',
    );
    this.name = 'MicrofoneNegadoError';
  }
}

/**
 * Começa a gravar. Devolve o controle de parar e cancelar.
 *
 * O erro de permissão vem tipado porque a tela reage diferente a ele: negar o
 * microfone não é falha do sistema, e a saída é um ajuste do navegador que só
 * a pessoa pode fazer.
 */
export async function gravar(formato: string | null): Promise<GravacaoEmCurso> {
  let trilhas: MediaStream;
  try {
    trilhas = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    throw new MicrofoneNegadoError();
  }

  const gravador = new MediaRecorder(trilhas, formato ? { mimeType: formato } : undefined);
  const pedacos: Blob[] = [];
  gravador.addEventListener('dataavailable', (evento) => {
    if (evento.data.size > 0) pedacos.push(evento.data);
  });
  gravador.start();

  /** O microfone volta para o sistema. Chamado nos DOIS finais possíveis. */
  const devolverMicrofone = (): void => {
    for (const trilha of trilhas.getTracks()) trilha.stop();
  };

  return {
    parar: () =>
      new Promise<File>((resolver) => {
        gravador.addEventListener(
          'stop',
          () => {
            devolverMicrofone();
            /*
             * O tipo sai do próprio gravador, e não do que pedimos.
             *
             * O Chrome aceita `audio/ogg` no construtor e grava WebM assim
             * mesmo. Confiar no que pedimos faria o arquivo sair rotulado como
             * OGG com WebM dentro — e o servidor, olhando o rótulo, não
             * converteria. O áudio chegaria quebrado no cliente.
             */
            const tipo = gravador.mimeType || 'audio/webm';
            const blob = new Blob(pedacos, { type: tipo });
            resolver(new File([blob], `audio-${Date.now()}.${extensaoDe(tipo)}`, { type: tipo }));
          },
          { once: true },
        );
        gravador.stop();
      }),

    cancelar: () => {
      // Parar o gravador antes de soltar as trilhas evita um `dataavailable`
      // final chegando depois de tudo já ter sido descartado.
      if (gravador.state !== 'inactive') gravador.stop();
      devolverMicrofone();
      pedacos.length = 0;
    },
  };
}

/** A extensão que combina com o conteúdo — o sistema do cliente abre pela extensão. */
function extensaoDe(mime: string): string {
  const base = semParametros(mime);
  if (base === 'audio/mp4') return 'm4a';
  if (base === 'audio/ogg') return 'ogg';
  return 'webm';
}

/** "0:07", "1:23" — o relógio de uma nota de voz. */
export function duracaoEmPalavras(segundos: number): string {
  const minutos = Math.floor(segundos / 60);
  return `${minutos}:${String(segundos % 60).padStart(2, '0')}`;
}
